import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

interface ReturnValue {
  path: number[];
  rootTitleId: number;
  activeTitleId: number;
}

/**
 * This hook is expected to be used on the Title Detail page.
 * Examples of expected `location.pathname` on the pages where this hook is used:
 *  "/titles/12"
 *  "/titles/12/45"
 *  "/titles/12/13/128"
 *  "/titles/12/13/128/4239"
 *
 *  The depth of title nesting is arbitrary.
 *
 *  For the "/titles/12/13/128/4239" pathname this hook will return:
 *
 *  {
 *    path: [12, 13, 128, 4239],
 *    rootTitleId: 12,
 *    activeTitleId: 4239
 *  }
 */
export const useTitleURLParser = (): ReturnValue => {
  const { pathname } = useLocation();

  const path: ReturnValue['path'] = pathname
    .replace(/^\/titles\/?/, '')
    .split('/')
    .filter(Boolean)
    .map(Number);

  const pathMemoized = useMemo(() => path, [path.join(',')]);
  const activeTitleId = pathMemoized.at(-1);

  if (activeTitleId === undefined) {
    throw new Error(`activeTitleId should not be undefined`);
  }

  return useMemo(
    () => ({
      path: pathMemoized,
      rootTitleId: pathMemoized[0],
      activeTitleId
    }),
    [pathMemoized]
  );
};
